import React from 'react';
import { TaskPresentationCard } from './TaskPresentationCard';
import { BarChart3 } from 'lucide-react';
import { 
  ServiceOrderWithTasks
} from '../../types/serviceOrder';
import { 
  Task,
  TaskAreaLabels,
  TaskAreaColors
} from '../../types/task';

interface ServiceOrderPresentationCardProps {
  serviceOrder: ServiceOrderWithTasks;
  onTaskStatusChange: (taskId: string, newStatus: Task['status']) => void;
  updatingTaskId?: string | null;
  exportMode?: boolean;
}

export const ServiceOrderPresentationCard: React.FC<ServiceOrderPresentationCardProps> = ({ 
  serviceOrder, 
  onTaskStatusChange,
  updatingTaskId,
  exportMode = false
}) => {


  const getSortedAreas = () => {
    return Object.keys(serviceOrder.tasksByArea).sort((a, b) => {
      const aData = serviceOrder.tasksByArea[a as keyof typeof serviceOrder.tasksByArea];
      const bData = serviceOrder.tasksByArea[b as keyof typeof serviceOrder.tasksByArea];
      return (bData?.totalHoras || 0) - (aData?.totalHoras || 0);
    });
  };

  const calculateAreaProgress = () => {
    const areas = getSortedAreas();
    return areas.map(area => {
      const areaKey = area as keyof typeof serviceOrder.tasksByArea;
      const areaData = serviceOrder.tasksByArea[areaKey];
      
      if (!areaData || areaData.tasks.length === 0) {
        return {
          area: areaKey,
          totalTasks: 0,
          completedTasks: 0,
          progress: 0,
          totalHours: 0
        };
      }

      const completedTasks = areaData.tasks.filter(task => task.status === 'encerrada').length;
      const progress = Math.round((completedTasks / areaData.tasks.length) * 100);

      return {
        area: areaKey,
        totalTasks: areaData.tasks.length,
        completedTasks,
        progress,
        totalHours: areaData.totalHoras
      };
    });
  };

  const areaProgressData = calculateAreaProgress();

  return (
    <div className="corporate-section">
      {/* Tasks by Area */}
      <div className="space-y-6 mt-8">
        
        {getSortedAreas().length === 0 ? (
          <div className="corporate-card">
            <div className="corporate-card-content text-center">
              <div className="corporate-text-tertiary">
                Nenhuma tarefa atribuída a esta ordem de serviço.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {getSortedAreas().map((area) => {
              const areaKey = area as keyof typeof serviceOrder.tasksByArea;
              const areaData = serviceOrder.tasksByArea[areaKey];
              const areaProgress = areaProgressData.find(progress => progress.area === areaKey);

              return (
                <div key={area} className="corporate-card-elevated">
                  <div className="corporate-card-header-accent">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`corporate-badge corporate-badge-primary ${TaskAreaColors[areaKey]}`}>
                          {TaskAreaLabels[areaKey]}
                        </span>
                      </div>
                      {areaProgress && areaProgress.totalTasks > 0 && !exportMode && (
                        <div className="flex items-center gap-3">
                          <span className="corporate-text-tertiary text-sm font-normal">
                            {areaProgress.totalHours}h estimadas
                          </span>
                          <span className="corporate-text-accent font-medium">
                            {areaProgress.progress}% concluído
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Area Progress Bar - Hidden in export mode */}
                    {areaProgress && areaProgress.totalTasks > 0 && !exportMode && (
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm corporate-text-secondary">
                            {areaProgress.completedTasks} de {areaProgress.totalTasks} tarefas concluídas
                          </span>
                        </div>
                        <div className="corporate-progress corporate-progress-lg">
                          <div 
                            className={`corporate-progress-fill ${
                              areaProgress.progress === 100 
                                ? 'success' 
                                : areaProgress.progress >= 75 
                                ? ''
                                : areaProgress.progress >= 50
                                ? 'warning'
                                : 'error'
                            }`}
                            style={{ width: `${areaProgress.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tasks List */}
                  <div className="p-0">
                    <div className="divide-y divide-gray-200">
                      {areaData?.tasks.map((task, index) => (
                        <div key={task._id}>
                          <TaskPresentationCard
                            task={task}
                            onStatusChange={onTaskStatusChange}
                            isUpdating={updatingTaskId === task._id}
                            exportMode={exportMode}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};